"use client";
import { useAuth } from "./AuthProvider";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { useRouter, usePathname } from "next/navigation";
import * as React from "react";
import { Search } from "lucide-react";

export function NavBar() {
  const { user, loading, signOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const avatarRef = React.useRef<HTMLDivElement>(null);
  // Search state
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClick);
    } else {
      document.removeEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    router.push("/signin");
  };

  // Define nav links (moved from SecondaryNavBar)
  const links = [
    { label: 'CROs', href: '/cros' },
    { label: 'Providers', href: '/providers' },
    { label: 'Platforms', href: '/platforms' },
    { label: 'Products', href: '/products' },
    { label: 'Technologies', href: '/technologies' },
  ];

  return (
    <>
      <nav className="w-full h-16 bg-neutral-900 backdrop-blur-md shadow-lg sticky top-0 z-50">
        <div className="w-full flex items-center justify-between h-full px-8">
          <div className="flex items-center gap-8">
            <a href="/" className="font-display flex items-center gap-2 text-3xl font-bold text-white tracking-tight hover:opacity-80 transition-opacity">
              LabAtlas
            </a>
            <div className="flex flex-row gap-2">
              {links.map(link => {
                const isActive = pathname === link.href;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`font-display text-lg font-semibold text-white hover:text-primary-300 transition-colors duration-150 mr-6 relative ${
                      isActive ? 'text-white' : ''
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-500"></div>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
          <div className="flex items-center">
            {/* Search icon and expandable input */}
            {searchOpen ? (
              <input
                ref={searchInputRef}
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onBlur={() => setSearchOpen(false)}
                placeholder="Search..."
                className="font-sans text-base w-48 px-3 py-2 rounded border border-border bg-gray-800 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2 transition-all duration-200"
                style={{ minWidth: 120 }}
              />
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 text-white hover:text-accent-foreground"
                aria-label="Open search"
                onClick={() => setSearchOpen(true)}
              >
                <Search className="w-10 h-10" />
              </Button>
            )}
            {!loading && !user && (
              <Button
                size="sm"
                className="font-display font-semibold bg-primary-500 text-white shadow-md hover:bg-primary-600 focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900"
                onClick={() => router.push("/signup")}
              >
                Sign Up
              </Button>
            )}
            {!loading && user && (
              <div ref={avatarRef} className="relative flex items-center">
                <div className="cursor-pointer" onClick={() => setMenuOpen((v) => !v)}>
                  <Avatar>
                    {user.photoURL ? (
                      <AvatarImage src={user.photoURL} alt={user.displayName || user.email || "User"} />
                    ) : (
                      <AvatarFallback>
                        {user.displayName
                          ? user.displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                          : user.email
                          ? user.email[0].toUpperCase()
                          : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5 text-muted-foreground"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 1115 0v.75a.75.75 0 01-.75.75h-13.5a.75.75 0 01-.75-.75V19.5z"
                              />
                            </svg>
                          )}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-40 rounded-md shadow-lg bg-background border border-border z-50 animate-fade-in">
                    <Button
                      variant="ghost"
                      className="font-sans text-sm font-medium w-full justify-start rounded-none px-4 py-2 text-left"
                      onClick={handleSignOut}
                    >
                      Sign Out
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
} 